import { ref, reactive, computed } from 'vue';
import { DirectUpload } from 'activestorage';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';

export function useBulkFileUpload() {
  const store = useStore();
  const { accountId: currentAccountId } = useAccount();
  const currentUser = store.getters.getCurrentUser;

  // Upload queue state
  const uploadQueue = ref([]);
  const isUploading = ref(false);
  const isDragOver = ref(false);

  // Statistics
  const stats = reactive({
    total: 0,
    completed: 0,
    failed: 0,
    inProgress: 0,
  });

  // Computed properties
  const progress = computed(() => {
    if (stats.total === 0) return 0;
    return Math.round((stats.completed / stats.total) * 100);
  });

  const hasUploads = computed(() => uploadQueue.value.length > 0);
  const isCompleted = computed(
    () => stats.completed === stats.total && stats.total > 0
  );
  const hasErrors = computed(() => stats.failed > 0);

  // Generate unique ID for each upload
  const generateUploadId = () =>
    `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Validate file type
  const isValidFile = file => {
    const allowedTypes = [
      'application/pdf',
      'text/plain',
      'text/csv',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml',
    ];

    const allowedExtensions = [
      '.pdf',
      '.txt',
      '.csv',
      '.docx',
      '.doc',
      '.xls',
      '.xlsx',
      '.jpg',
      '.jpeg',
      '.png',
      '.gif',
      '.webp',
      '.svg',
    ];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext =>
      fileName.endsWith(ext)
    );

    return allowedTypes.includes(file.type) || hasValidExtension;
  };

  // Get file type icon
  const getFileIcon = file => {
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith('.pdf')) return 'i-lucide-file-text';
    if (fileName.endsWith('.txt')) return 'i-lucide-file-text';
    if (
      fileName.endsWith('.csv') ||
      fileName.endsWith('.xls') ||
      fileName.endsWith('.xlsx')
    )
      return 'i-lucide-file-spreadsheet';
    if (fileName.endsWith('.docx') || fileName.endsWith('.doc'))
      return 'i-lucide-file-text';
    if (
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg') ||
      fileName.endsWith('.png') ||
      fileName.endsWith('.gif') ||
      fileName.endsWith('.webp') ||
      fileName.endsWith('.svg')
    )
      return 'i-lucide-image';
    return 'i-lucide-file';
  };

  // Add files to upload queue
  const addFilesToQueue = (files, knowledgebaseId) => {
    const newUploads = Array.from(files)
      .filter(file => {
        if (!isValidFile(file)) {
          useAlert(
            `File "${file.name}" is not a supported file type.`,
            'warning'
          );
          return false;
        }

        if (file.size > 100 * 1024 * 1024) {
          // 100MB limit
          useAlert(
            `File "${file.name}" is too large. Maximum size is 100MB.`,
            'warning'
          );
          return false;
        }
        return true;
      })
      .map(file => ({
        id: generateUploadId(),
        file,
        name: file.name,
        size: file.size,
        status: 'pending', // pending, uploading, processing, completed, failed
        progress: 0,
        error: null,
        icon: getFileIcon(file),
        knowledgebaseId,
        createdAt: new Date(),
      }));

    uploadQueue.value.push(...newUploads);
    stats.total += newUploads.length;

    return newUploads;
  };

  // Remove upload from queue
  const removeFromQueue = uploadId => {
    const index = uploadQueue.value.findIndex(upload => upload.id === uploadId);
    if (index !== -1) {
      const upload = uploadQueue.value[index];
      uploadQueue.value.splice(index, 1);

      // Update stats
      if (upload.status === 'completed') stats.completed -= 1;
      else if (upload.status === 'failed') stats.failed -= 1;
      stats.total -= 1;
    }
  };

  // Clear completed uploads
  const clearCompleted = () => {
    const completedCount = uploadQueue.value.filter(
      upload => upload.status === 'completed'
    ).length;
    uploadQueue.value = uploadQueue.value.filter(
      upload => upload.status !== 'completed'
    );
    stats.completed -= completedCount;
    stats.total -= completedCount;
  };

  // Clear all uploads
  const clearAll = () => {
    uploadQueue.value = [];
    stats.total = 0;
    stats.completed = 0;
    stats.failed = 0;
    stats.inProgress = 0;
  };

  // Upload single file
  const uploadSingleFile = async uploadItem => {
    try {
      uploadItem.status = 'uploading';
      stats.inProgress += 1;

      // Create DirectUpload instance
      const upload = new DirectUpload(
        uploadItem.file,
        `/api/v1/accounts/${currentAccountId.value}/knowledgebases/${uploadItem.knowledgebaseId}/direct_uploads`,
        {
          directUploadWillCreateBlobWithXHR: xhr => {
            xhr.setRequestHeader('api_access_token', currentUser.access_token);

            // Track upload progress
            xhr.upload.addEventListener('progress', event => {
              if (event.lengthComputable) {
                uploadItem.progress = Math.round(
                  (event.loaded / event.total) * 100
                );
              }
            });
          },
        }
      );

      // Upload file to storage
      const blobData = await new Promise((resolve, reject) => {
        upload.create((error, blob) => {
          if (error) {
            reject(error);
          } else {
            resolve(blob);
          }
        });
      });

      // Create record
      uploadItem.status = 'processing';
      uploadItem.progress = 100;

      await store.dispatch('records/create', {
        record: { file: blobData.signed_id },
        knowledgebaseId: uploadItem.knowledgebaseId,
      });

      uploadItem.status = 'completed';
      stats.completed += 1;
      stats.inProgress -= 1;
    } catch (error) {
      uploadItem.status = 'failed';
      uploadItem.error = error?.message || 'Upload failed';
      stats.failed += 1;
      stats.inProgress -= 1;

      // eslint-disable-next-line no-console
      console.error('Upload failed:', error);
    }
  };

  // Process upload queue
  const processQueue = async () => {
    if (isUploading.value) return;

    isUploading.value = true;

    try {
      const pendingUploads = uploadQueue.value.filter(
        upload => upload.status === 'pending'
      );

      // Process uploads sequentially (one at a time)
      await pendingUploads.reduce(async (promise, upload) => {
        await promise;
        return uploadSingleFile(upload);
      }, Promise.resolve());
    } finally {
      isUploading.value = false;
    }
  };

  // Retry failed upload
  const retryUpload = async uploadId => {
    const upload = uploadQueue.value.find(u => u.id === uploadId);
    if (upload && upload.status === 'failed') {
      upload.status = 'pending';
      upload.error = null;
      upload.progress = 0;
      stats.failed -= 1;

      await uploadSingleFile(upload);
    }
  };

  // Format file size
  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  // Drag and drop handlers
  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    isDragOver.value = true;
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the main container
    if (!e.currentTarget.contains(e.relatedTarget)) {
      isDragOver.value = false;
    }
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e, knowledgebaseId) => {
    e.preventDefault();
    e.stopPropagation();
    isDragOver.value = false;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      addFilesToQueue(files, knowledgebaseId);
      // Auto-start processing
      setTimeout(() => processQueue(), 100);
    }
  };

  return {
    // State
    uploadQueue,
    isUploading,
    isDragOver,
    stats,

    // Computed
    progress,
    hasUploads,
    isCompleted,
    hasErrors,

    // Methods
    addFilesToQueue,
    removeFromQueue,
    clearCompleted,
    clearAll,
    processQueue,
    retryUpload,
    formatFileSize,

    // Drag and drop handlers
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  };
}
